"use client";

import * as React from "react";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Loader2, ArrowLeft, Eye, EyeOff, CheckCircle, XCircle } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { icons } from "../../assets/icons";
import { recuperarClavePaso2 } from "../../api/usuarioServices";
import Swal from "sweetalert2";

interface PasswordRequirement {
  text: string;
  regex: RegExp;
  met: boolean;
}

export default function RecoverPasswordStep2() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = React.useState(false);

  const [formData, setFormData] = React.useState({
    email: location.state?.email || "",
    pin: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [formErrors, setFormErrors] = React.useState({
    email: "",
    pin: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [passwordRequirements, setPasswordRequirements] = React.useState<PasswordRequirement[]>([
    { text: "Mínimo 8 caracteres", regex: /.{8,}/, met: false },
    { text: "Al menos una mayúscula", regex: /[A-Z]/, met: false },
    { text: "Al menos un número", regex: /\d/, met: false },
    { text: "Al menos una minúscula", regex: /[a-z]/, met: false },
  ]);

  // Actualizar validaciones de contraseña en tiempo real
  React.useEffect(() => {
    const newPassword = formData.newPassword;
    setPasswordRequirements(prev =>
      prev.map(req => ({
        ...req,
        met: req.regex.test(newPassword)
      }))
    );
  }, [formData.newPassword]);

  const isPasswordValid = () => {
    return passwordRequirements.every(req => req.met);
  };

  const validateForm = () => {
    const errors = {
      email: "",
      pin: "",
      newPassword: "",
      confirmPassword: "",
    };

    if (!formData.email) {
      errors.email = "El correo electrónico es obligatorio";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "El formato del correo electrónico no es válido";
    }

    if (!formData.pin) {
      errors.pin = "El PIN de recuperación es obligatorio";
    } else if (formData.pin.length < 4) {
      errors.pin = "El PIN debe tener al menos 4 caracteres";
    }

    if (!formData.newPassword) {
      errors.newPassword = "La contraseña nueva es obligatoria";
    } else if (!isPasswordValid()) {
      errors.newPassword = "La contraseña no cumple con los requisitos de seguridad";
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = "La confirmación de contraseña es obligatoria";
    } else if (formData.newPassword !== formData.confirmPassword) {
      errors.confirmPassword = "Las contraseñas no coinciden";
    }

    setFormErrors(errors);
    return !errors.email && !errors.pin && !errors.newPassword && !errors.confirmPassword;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!validateForm()) return;

    try {
      setIsSubmitting(true);

      const response = await recuperarClavePaso2({
        email: formData.email,
        "pin recuperacion": formData.pin,
        "Contrasena nueva": formData.newPassword,
        "Contrasena nueva confirmacion": formData.confirmPassword,
      });

      if (response.code === "1" || response.code === 1) {
        await Swal.fire({
          icon: "success",
          title: "¡Contraseña actualizada!",
          text: response.message || "Contraseña ha sido modificada exitosamente",
          confirmButtonText: "Ir al inicio de sesión",
          confirmButtonColor: "#4a5568",
        });

        // Navegar al login
        navigate("/login");
      } else {
        await Swal.fire({
          icon: "error",
          title: "Error",
          text: response.message || "No se pudo actualizar la contraseña",
          confirmButtonText: "Aceptar",
          confirmButtonColor: "#4a5568",
        });
      }
    } catch (error: any) {
      console.error("Error en RecuperarClavePaso2:", error);

      let errorMessage = "Error al actualizar la contraseña";
      if (error?.serverMessage) {
        errorMessage = error.serverMessage;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      await Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#4a5568",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-gray-100 via-gray-200 to-gray-300 px-4">
      <Card
        className="w-full max-w-md shadow-xl animate-slideUp border rounded-2xl"
        style={{ backgroundColor: "rgb(247,247,247)" }}
      >
        <CardContent>
          <div className="flex justify-center mb-6">
            <img
              src={icons.logo}
              alt="App Logo"
              className="w-40 h-auto object-contain"
            />
          </div>

          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-800 text-center">
              Recuperar Contraseña
            </h2>
            <p className="text-sm text-gray-600 text-center mt-2">
              Paso 2 de 2
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex flex-col space-y-2">
              <Label htmlFor="email" className="text-gray-700">
                Correo electrónico
              </Label>
              <Input
                id="email"
                placeholder="usuario@easyph.com"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                className="bg-white text-gray-900 border border-gray-300 focus:border-military-500 focus:ring-military-500 rounded-lg"
              />
              {formErrors.email && (
                <p className="text-sm text-red-600">{formErrors.email}</p>
              )}
            </div>

            <div className="flex flex-col space-y-2">
              <Label htmlFor="pin" className="text-gray-700">
                PIN de recuperación
              </Label>
              <Input
                id="pin"
                placeholder="Ingresa el PIN recibido en tu correo"
                type="text"
                value={formData.pin}
                onChange={(e) =>
                  setFormData({ ...formData, pin: e.target.value.toUpperCase() })
                }
                className="bg-white text-gray-900 border border-gray-300 focus:border-military-500 focus:ring-military-500 rounded-lg"
              />
              {formErrors.pin && (
                <p className="text-sm text-red-600">{formErrors.pin}</p>
              )}
            </div>

            <div className="flex flex-col space-y-2">
              <Label htmlFor="newPassword" className="text-gray-700">
                Nueva contraseña
              </Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showPassword ? "text" : "password"}
                  placeholder="********"
                  value={formData.newPassword}
                  onChange={(e) =>
                    setFormData({ ...formData, newPassword: e.target.value })
                  }
                  className="bg-white text-gray-900 border border-gray-300 focus:border-military-500 focus:ring-military-500 rounded-lg pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-military-600 transition"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {formErrors.newPassword && (
                <p className="text-sm text-red-600">{formErrors.newPassword}</p>
              )}
            </div>

            {/* Requisitos de contraseña - Solo mostrar si hay texto y no todos están cumplidos */}
            {formData.newPassword && !isPasswordValid() && (
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Requisitos de contraseña:</h4>
                <div className="space-y-2">
                  {passwordRequirements.map((req, index) => (
                    <div key={index} className="flex items-center gap-2">
                      {req.met ? (
                        <CheckCircle size={16} className="text-green-500" />
                      ) : (
                        <XCircle size={16} className="text-red-500" />
                      )}
                      <span className={`text-sm ${req.met ? 'text-green-700' : 'text-red-600'}`}>
                        {req.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-col space-y-2">
              <Label htmlFor="confirmPassword" className="text-gray-700">
                Confirmar nueva contraseña
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showPasswordConfirm ? "text" : "password"}
                  placeholder="********"
                  value={formData.confirmPassword}
                  onChange={(e) =>
                    setFormData({ ...formData, confirmPassword: e.target.value })
                  }
                  className="bg-white text-gray-900 border border-gray-300 focus:border-military-500 focus:ring-military-500 rounded-lg pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-military-600 transition"
                >
                  {showPasswordConfirm ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {formErrors.confirmPassword && (
                <p className="text-sm text-red-600">{formErrors.confirmPassword}</p>
              )}
            </div>

            <Button
              type="submit"
              disabled={isSubmitting || !isPasswordValid() || formData.newPassword !== formData.confirmPassword}
              className="w-full bg-military-500 hover:bg-military-600 text-white shadow-md rounded-lg transition disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Actualizando contraseña...
                </>
              ) : (
                "Actualizar contraseña"
              )}
            </Button>
          </form>
        </CardContent>

        <CardFooter className="flex justify-center">
          <Button
            variant="link"
            onClick={() => navigate("/login")}
            className="text-gray-600 hover:text-military-600 flex items-center gap-1"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver al inicio de sesión
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
